import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { LoaderCircle, Monitor, Smartphone, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

export type StorefrontPreviewDevice = 'desktop' | 'mobile'

export interface StorefrontPreviewStatus {
  /** `unsaved`: the preview shows the last save, and the form has changes since. */
  state: 'loading' | 'syncing' | 'ready' | 'unsaved' | 'error'
  label: string
}

export interface StorefrontPreviewViewOption<View extends string> {
  value: View
  label: string
  ariaLabel: string
  icon: LucideIcon
}

export const STOREFRONT_PREVIEW_DEVICES = [
  { value: 'desktop', label: 'Desktop', ariaLabel: 'Desktop preview', icon: Monitor },
  { value: 'mobile', label: 'Mobile', ariaLabel: 'Mobile preview', icon: Smartphone },
] as const satisfies ReadonlyArray<StorefrontPreviewViewOption<StorefrontPreviewDevice>>

/** The dark preview window around a storefront: status, view switch and optional page tabs. */
export function StorefrontPreviewPanel<View extends string>({
  status,
  view,
  views,
  onViewChange,
  pageTabs = null,
  className,
  children,
}: {
  status: StorefrontPreviewStatus
  view: View
  views: ReadonlyArray<StorefrontPreviewViewOption<View>>
  onViewChange: (view: View) => void
  pageTabs?: ReactNode
  className?: string | null
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-[32px] border border-black/10 bg-zinc-950 shadow-[0_30px_90px_-30px_rgba(15,23,42,0.7)]',
        className
      )}
    >
      <div className="border-b border-white/10 bg-zinc-950 px-3 py-2 text-white">
        <div className="flex flex-col gap-2">
          <div className="flex min-h-9 flex-wrap items-center justify-between gap-2">
            <Typography variant="eyebrow" className="shrink-0 text-zinc-400">
              Preview
            </Typography>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="inline-flex h-8 items-center gap-2 rounded-full border border-white/10 bg-white/6 px-3 text-xs font-medium text-zinc-200">
                {status.state === 'ready' ? (
                  <span className="size-2 rounded-full bg-success" />
                ) : status.state === 'unsaved' ? (
                  <span className="size-2 rounded-full bg-warning" />
                ) : status.state === 'error' ? (
                  <span className="size-2 rounded-full bg-destructive" />
                ) : (
                  <LoaderCircle className="size-3.5 animate-spin" />
                )}
                <span>{status.label}</span>
              </div>

              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1">
                {views.map(({ value, label, ariaLabel, icon: Icon }) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={view === value ? 'secondary' : 'ghost'}
                    aria-label={ariaLabel}
                    className={cn(
                      'h-7 rounded-full px-2',
                      view === value
                        ? 'text-zinc-950 hover:text-zinc-950 dark:text-white dark:hover:text-white'
                        : 'text-white hover:bg-white/10 hover:text-white'
                    )}
                    onClick={() => onViewChange(value)}
                  >
                    <Icon className="size-4" />
                    <span className="hidden 2xl:inline">{label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {pageTabs}
        </div>
      </div>

      {children}
    </div>
  )
}

/** Shown inside the panel while the storefront can't be shown. */
export function StorefrontPreviewMessage({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <div className="p-6">
      <div className="rounded-[28px] border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-white">
        <Typography variant="emphasis" className="block font-medium text-white">
          {title}
        </Typography>
        <Typography variant="supporting" className="mt-2 block text-zinc-300">
          {children}
        </Typography>
      </div>
    </div>
  )
}

/** The storefront iframe's frame, sized for the device, covered until the page has loaded. */
export function StorefrontPreviewViewport({
  device,
  isLoading,
  className,
  children,
}: {
  device: StorefrontPreviewDevice
  isLoading: boolean
  className?: string | null
  children: ReactNode
}) {
  return (
    <div className="min-h-0 flex-1 overflow-hidden bg-zinc-900 p-2">
      <div
        className={cn(
          'relative overflow-hidden rounded-[28px] border border-black/10 bg-white shadow-2xl transition-all',
          device === 'mobile'
            ? 'mx-auto w-full max-w-[390px] h-[min(720px,calc(100svh-15rem))]'
            : 'w-full h-[min(760px,calc(100svh-13rem))]',
          className
        )}
      >
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/94 backdrop-blur-sm">
            <div className="space-y-2 text-center text-zinc-900">
              <div className="inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-interaction">
                <LoaderCircle className="size-5 animate-spin" />
              </div>
              <div className="space-y-1">
                <Typography variant="emphasis" className="block font-medium">
                  Loading storefront preview
                </Typography>
                <Typography variant="supporting" className="block text-zinc-600">
                  Opening the real storefront renderer for this page.
                </Typography>
              </div>
            </div>
          </div>
        ) : null}

        {children}
      </div>
    </div>
  )
}
